import {
  Box,
  Button,
  Flex,
  Group,
  Modal,
  Radio,
  rem,
  Select,
  Space,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import classes from "../TransInter.module.scss";
import { useTranslation } from "react-i18next";
import { TransInterStepOneProps } from "../types";
import {
  IconArrowsLeftRight,
  IconCheck,
  IconChevronDown,
  IconSearch,
  IconWallet,
} from "@tabler/icons-react";
import { useTransFormContext } from "../TransInterFormContext";
import FromAccount from "@/components/common/from-account/FromAccount";
import { useGetInternationalTransferMasterDataQuery } from "@/services/internationalTransferAPI";
import { getCurrencyScale, formatMoney } from "../currencyFormat";

// Định dạng khi đang gõ: nhóm nghìn bằng dấu phẩy, cho phép nhập thập phân
// tối đa theo scale của loại tiền (scale 0 → không cho nhập dấu chấm).
const formatAmountInput = (value: string, scale: number) => {
  let s = value.replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  if (scale === 0) s = s.replace(/\./g, "");
  if (s === "") return "";
  const [intPart, decPart = ""] = s.split(".");
  const grouped = intPart ? Number(intPart).toLocaleString("en-US") : "0";
  if (s.includes(".") && scale > 0) {
    return `${grouped}.${decPart.slice(0, scale)}`;
  }
  return grouped;
};

// Thẻ tài khoản nhỏ (tài khoản trả phí / tài khoản mua ngoại tệ).
const AccountMiniCard: React.FC<{
  name?: string;
  number?: string;
  rightText?: string;
  withChevron?: boolean;
}> = ({ name, number, rightText, withChevron }) => (
  <Box
    className={classes.infoSource}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    }}
  >
    <Group spacing={10} noWrap>
      <Box
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: "#EAF2FE",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <IconWallet size={20} color="#2A81D0" />
      </Box>
      <Stack spacing={2}>
        <Text fw={600} fz={14} c="#212936">
          {name || "—"}
        </Text>
        <Text fz={12} c="#6B7280">
          {number || "—"}
        </Text>
      </Stack>
    </Group>
    <Group spacing={8} noWrap>
      {rightText && (
        <Text fw={600} fz={14} c="#212936">
          {rightText}
        </Text>
      )}
      {withChevron && <IconChevronDown size={16} color="#6B7280" />}
    </Group>
  </Box>
);

const TransInterStepOne: React.FC<TransInterStepOneProps> = ({
  sourceLinkId,
  sources,
  onChangeSourceLink,
  onSubmit,
}) => {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.language || "vi").startsWith("en");
  // #55961: nut "Check the code" nam trong rightSection nen phai bao be rong.
  // Nhan tieng Anh dai hon tieng Viet ("Kiem tra ma"), be rong tinh theo tieng
  // Viet lam nut tran ra ngoai o nhap.
  const checkCodeButtonWidth = (checked: boolean) => {
    if (isEnglish) return checked ? 124 : 148;
    return checked ? 116 : 108;
  };
  const form = useTransFormContext();
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] =
    useDisclosure(false);

  const { data: masterData } = useGetInternationalTransferMasterDataQuery();
  const currencyData = useMemo(() => {
    const list = masterData?.body?.currencies || [];
    if (list.length === 0) return [{ value: "USD", label: "USD" }];
    return list.map((c) => ({ value: c.code, label: c.code }));
  }, [masterData]);

  const selectedSource = sources.find((s) => s.id === sourceLinkId);
  const currency = form.values.currency || "USD";
  const amount = form.values.amount || "";
  const needBuy = form.values.needBuyCurrency || "N";
  const feeType = form.values.feeType || "OUR";
  const sourceAccountNo =
    selectedSource?.sourceNumber || selectedSource?.sourceObjectNumber || "";
  // Số chữ số thập phân theo loại tiền (BR-06): VND/JPY=0, KWD/BHD…=3, còn lại=2.
  const currencyScale = getCurrencyScale(currency);
  const currencyRate = masterData?.body?.currencies?.find(
    (c) => c.code === currency
  )?.vndRate;

  // ===== Mã báo giá (tỷ giá thỏa thuận – rateType = DEAL) =====
  // Lưu tỷ giá đã "kiểm tra" theo đúng mã báo giá vừa nhập. Khi KH sửa mã thì
  // coi như chưa kiểm tra (code khác code đã lưu). Chưa có API → tạm mock rate.
  const [dealInfo, setDealInfo] = useState<{ code: string; rate: number } | null>(
    null
  );
  const isDeal = form.values.rateType === "DEAL";
  const dealCode = (form.values.dealReferenceCode || "").trim();
  const dealChecked = isDeal && dealInfo != null && dealInfo.code === dealCode;

  // Tỷ giá dùng để quy đổi:
  //  - Tỷ giá tự động → tỷ giá tham chiếu (vndRate).
  //  - Tỷ giá thỏa thuận → tỷ giá trả về sau khi kiểm tra mã báo giá.
  let effectiveRate: number | undefined;
  if (isDeal) {
    effectiveRate = dealChecked ? dealInfo?.rate : undefined;
  } else {
    effectiveRate = Number(currencyRate) || undefined;
  }

  const handleCheckDeal = useCallback(() => {
    if (!dealCode) return;
    // TODO: thay bằng API kiểm tra mã báo giá thật (trả về tỷ giá đã thỏa thuận).
    setDealInfo({ code: dealCode, rate: Number(currencyRate) || 0 });
  }, [dealCode, currencyRate]);

  // ===== Tính phí / tổng tiền / quy đổi REALTIME ngay trên FE (dùng vndRate + decimalScale) =====
  // Ước tính phí 0.1% (tối thiểu 5 theo NT). Phí/tỉ giá chính thức do BE chốt ở bước duyệt cuối.
  const FEE_RATE = 0.001;
  const FEE_MIN = 5;
  const toNum = (v?: string) =>
    Number(String(v || "").replace(/[^\d.]/g, "")) || 0;
  const roundScale = (v: number, s: number) => {
    const f = 10 ** s;
    return Math.round(v * f) / f;
  };
  const fmt = (n: number, scale: number) => formatMoney(n, scale);
  // VND luôn 0 số thập phân, phân tách nghìn bằng dấu phẩy.
  const fmtVnd = (n: number) => formatMoney(n, 0);

  const calc = useMemo(() => {
    const amt = toNum(amount);
    const rate = Number(effectiveRate) || 0;
    const feeForeign =
      amt > 0
        ? roundScale(Math.max(amt * FEE_RATE, FEE_MIN), currencyScale)
        : 0;
    const feeVnd = Math.round(feeForeign * rate);
    let totalForeign = amt;
    if (feeType === "OUR") totalForeign = amt + feeForeign;
    else if (feeType === "SHA") totalForeign = amt + feeForeign / 2;
    const totalVnd = Math.round(totalForeign * rate);
    // Ngoại tệ mua → quy đổi ra VND (auto) để hiển thị ở ô "Số tiền quy đổi".
    const buyForeign = toNum(form.values.foreignAmount);
    const convertedVnd = Math.round(buyForeign * rate);
    // Số tiền ghi nợ = tiền chuyển + phí (KHÔNG cộng tiền mua ngoại tệ), quy về
    // loại tiền của TK nguồn:
    //  - TK nguồn cùng loại ngoại tệ chuyển → giữ nguyên (không quy đổi).
    //  - TK nguồn khác loại → quy đổi theo tỷ giá bán chuyển khoản của NH.
    const accountCcy = selectedSource?.currency || "VND";
    const accountRate =
      accountCcy === "VND"
        ? 1
        : Number(
            masterData?.body?.currencies?.find((c) => c.code === accountCcy)
              ?.vndRate
          ) || 1;
    const debitBase = totalForeign;
    const debitCcy = accountCcy;
    const debitScale = accountCcy === "VND" ? 0 : getCurrencyScale(accountCcy);
    const debitVal =
      accountCcy === currency
        ? debitBase
        : roundScale((debitBase * rate) / accountRate, debitScale);
    // Số tiền giao dịch quy về loại tiền TK nguồn (chỉ tiền chuyển, không phí).
    const txnAccountVal =
      accountCcy === currency
        ? amt
        : roundScale((amt * rate) / accountRate, debitScale);
    return {
      feeForeign,
      feeVnd,
      totalForeign,
      totalVnd,
      convertedVnd,
      debitCcy,
      debitVal,
      debitScale,
      // Số tiền giao dịch = chỉ tiền chuyển quốc tế (không gồm phí & NT mua).
      txnForeign: amt,
      txnAccountVal,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    amount,
    currency,
    feeType,
    effectiveRate,
    currencyScale,
    form.values.foreignAmount,
    needBuy,
    selectedSource,
    masterData,
  ]);

  const debitAmountDisplay = `${fmt(calc.debitVal, calc.debitScale)} ${
    calc.debitCcy
  }`;

  // Validate mềm: số ngoại tệ mua phải ≤ số tiền giao dịch (khi có mua NT).
  const buyExceeds =
    needBuy === "Y" && toNum(form.values.foreignAmount) > toNum(amount);

  // Kiểm tra vượt số dư: tổng tiền ghi nợ (quy đổi VND + phí) > số dư khả dụng.
  const rawBalance = String(selectedSource?.availableBalance ?? "").replace(
    /,/g,
    ""
  );
  const availableBalance = rawBalance === "" ? NaN : Number(rawBalance);
  const accountCcy = selectedSource?.currency || "VND";
  // Loại tiền TK nguồn khác loại ngoại tệ chuyển → bắt buộc mua ngoại tệ.
  const mustBuyCurrency = accountCcy !== currency;
  // Quy về loại tiền của TK nguồn để so sánh với số dư (TK VND → dùng tổng quy đổi VND).
  const neededInAccountCcy =
    accountCcy === "VND" ? calc.totalVnd : calc.totalForeign;
  const insufficientBalance =
    toNum(amount) > 0 &&
    Number.isFinite(availableBalance) &&
    neededInAccountCcy > availableBalance;

  // Tỷ giá thỏa thuận (DEAL): bắt buộc nhập mã báo giá VÀ đã kiểm tra để lấy tỷ giá.
  const missingDealRate = needBuy === "Y" && isDeal && !dealChecked;

  const canContinue =
    toNum(amount) > 0 && !buyExceeds && !insufficientBalance && !missingDealRate;

  // Bắt buộc "Cần mua ngoại tệ" = Có khi TK nguồn khác loại ngoại tệ chuyển.
  useEffect(() => {
    if (mustBuyCurrency && needBuy !== "Y") {
      form.setFieldValue("needBuyCurrency", "Y");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mustBuyCurrency, needBuy]);

  // Số ngoại tệ mua = vừa đủ để chuyển (không thừa/thiếu):
  //  - TK nguồn khác loại NT → không có sẵn NT đó → mua = toàn bộ số chuyển.
  //  - TK nguồn cùng loại NT → mua = số chuyển − số dư hiện có (nếu thiếu).
  const foreignHeld =
    accountCcy === currency && Number.isFinite(availableBalance)
      ? availableBalance
      : 0;
  const autoBuyForeign = roundScale(
    Math.max(toNum(amount) - foreignHeld, 0),
    currencyScale
  );

  // Tự động fill "Số ngoại tệ mua" khi cần mua ngoại tệ.
  useEffect(() => {
    if (needBuy !== "Y") return;
    const formatted =
      autoBuyForeign > 0 ? fmt(autoBuyForeign, currencyScale) : "";
    if (form.values.foreignAmount !== formatted) {
      form.setFieldValue("foreignAmount", formatted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needBuy, autoBuyForeign, currencyScale]);

  const handleConfirmReset = useCallback(() => {
    form.reset();
    form.setValues({
      currency: "USD",
      needBuyCurrency: "N",
      feeType: "OUR",
      rateType: "AUTO",
      documents: [],
      debtDocumentLater: false,
    });
    setDealInfo(null);
    closeConfirm();
  }, [form, closeConfirm]);

  // Đổi loại ngoại tệ → tỷ giá thỏa thuận cũ không còn đúng, buộc kiểm tra lại.
  useEffect(() => {
    setDealInfo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  const handleAmountChange = (
    field: "amount" | "foreignAmount",
    value: string
  ) => {
    form.setFieldValue(field, formatAmountInput(value, currencyScale));
  };

  // Khi rời ô nhập: bổ sung đủ số thập phân theo loại tiền (vd "100" → "100.00").
  const handleAmountBlur = (field: "amount" | "foreignAmount") => {
    const v = form.values[field];
    if (v) form.setFieldValue(field, fmt(toNum(v), currencyScale));
  };

  return (
    <>
      <Box className={classes.sourceAccount} w="62%">
        <Stack spacing={20}>
          <FromAccount
            title={t("DEBIT_ACCOUNT_LABEL")}
            sourceLinkId={sourceLinkId}
            sources={sources}
            onChangeSourceLinkId={onChangeSourceLink}
          />
          <Text fw={600} fz={16} c="#212936">
            {t("INFORMATION_TRANSFER") || t("INFORMATION_TRANSFER")}
          </Text>
          <Flex gap={16}>
            <Select
              data={currencyData}
              w={185}
              label={t("FOREIGN_CURRENCY")}
              radius={8}
              rightSection={<IconChevronDown width={16} />}
              value={currency}
              onChange={(value) =>
                form.setFieldValue("currency", value || "USD")
              }
            />
            <TextInput
              label={t("AMOUNT")}
              rightSection={currency}
              style={{ flex: 1 }}
              radius={8}
              styles={{ rightSection: { marginRight: "5px", width: 46 } }}
              value={amount}
              error={form.errors.amount}
              onChange={(e) =>
                handleAmountChange("amount", e.currentTarget.value)
              }
              onBlur={() => handleAmountBlur("amount")}
            />
          </Flex>

          {/* Số tiền ghi nợ (tính tự động) */}
          <Group spacing={8}>
            <Text fz={14} c="#6B7280">
              {t("DEBIT_AMOUNT")}
            </Text>
            <Text fz={14} fw={600} c="#212936">
              {debitAmountDisplay}
            </Text>
          </Group>
          <Text fz={13} c="#6B7280" mt={-12}>
            {t("AMOUNT_ADJUSTS_IF_FX_PURCHASED")}
          </Text>
          {insufficientBalance && (
            <Text fz={13} c="red" mt={-12}>
              {t("INSUFFICIENT_BALANCE_FOR_TRANSACTION")}
            </Text>
          )}

          {/* Cần mua ngoại tệ */}
          <Box className={classes.infoTrans}>
            <Radio.Group
              label={t("NEED_TO_BUY_FOREIGN_CURRENCY")}
              value={needBuy}
              onChange={(v) => {
                // Khác loại ngoại tệ → bắt buộc mua, không cho chọn "Không".
                if (mustBuyCurrency) return;
                form.setFieldValue("needBuyCurrency", v);
              }}
            >
              <Group mt="xs">
                <Radio value="Y" label={t("YES")} />
                <Radio value="N" label={t("NO")} disabled={mustBuyCurrency} />
              </Group>
            </Radio.Group>
            {mustBuyCurrency && (
              <Text fz={13} c="#6B7280" mt={8}>
                {t("SOURCE_ACCOUNT_OPEN_PAREN")}{accountCcy}{t("DIFFERS_FROM_TRANSFER_CURRENCY")}
                {currency}{t("FOREIGN_CURRENCY_PURCHASE_REQUIRED")}
              </Text>
            )}

            {needBuy === "Y" && (
              <Stack spacing={14} mt={16}>
                <Box>
                  <Text fz={14} c="#6B7280" mb={6}>
                    {t("FOREIGN_CURRENCY_PURCHASE_ACCOUNT")}
                  </Text>
                  <AccountMiniCard
                    name={selectedSource?.sourceName}
                    number={
                      selectedSource?.sourceNumber ||
                      selectedSource?.sourceObjectNumber
                    }
                    rightText={
                      selectedSource?.availableBalanceDisplay ||
                      "100,000,000 VND"
                    }
                    withChevron
                  />
                </Box>
                <Flex gap={12}>
                  <Select
                    label={t("EXCHANGE_RATE_TYPE")}
                    radius={8}
                    data={[
                      { value: "AUTO", label: t("AUTOMATIC_EXCHANGE_RATE") },
                      { value: "DEAL", label: t("AGREED_EXCHANGE_RATE") },
                    ]}
                    rightSection={<IconChevronDown width={16} />}
                    value={form.values.rateType || "AUTO"}
                    onChange={(v) =>
                      form.setFieldValue("rateType", v || "AUTO")
                    }
                    style={{ flex: 1 }}
                  />
                  {isDeal ? (
                    <TextInput
                      label={t("QUOTE_CODE")}
                      placeholder={t("ENTER_AGREED_QUOTE_CODE")}
                      radius={8}
                      style={{ flex: 1 }}
                      value={form.values.dealReferenceCode || ""}
                      error={
                        isDeal && !dealCode
                          ? t("PLEASE_ENTER_QUOTE_CODE")
                          : undefined
                      }
                      onChange={(e) =>
                        form.setFieldValue(
                          "dealReferenceCode",
                          e.currentTarget.value
                        )
                      }
                      // #55961: nhan tieng Anh dai hon tieng Viet ("Check the
                      // code" vs "Kiem tra ma") nen be rong co dinh tinh theo
                      // tieng Viet lam nut tran ra ngoai o nhap.
                      rightSectionWidth={checkCodeButtonWidth(dealChecked)}
                      rightSection={
                        <Button
                          variant="subtle"
                          size="xs"
                          radius={8}
                          disabled={!dealCode || dealChecked}
                          onClick={handleCheckDeal}
                          leftIcon={
                            dealChecked ? (
                              <IconCheck size={14} />
                            ) : (
                              <IconSearch size={14} />
                            )
                          }
                          styles={{
                            root: { paddingLeft: 8, paddingRight: 8 },
                            leftIcon: { marginRight: 4 },
                          }}
                          color={dealChecked ? "teal" : "blue"}
                        >
                          {dealChecked ? t("CHECKED") : t("CHECK_CODE")}
                        </Button>
                      }
                    />
                  ) : (
                    <Box style={{ flex: 1 }} />
                  )}
                </Flex>
                <Flex gap={12} align="flex-end">
                  <TextInput
                    label={t("FOREIGN_CURRENCY_AMOUNT_BOUGHT")}
                    style={{ flex: 1 }}
                    radius={8}
                    readOnly
                    rightSectionWidth={80}
                    rightSection={
                      <Select
                        data={currencyData}
                        value={currency}
                        onChange={(value) =>
                          form.setFieldValue("currency", value || "USD")
                        }
                        variant="unstyled"
                        rightSection={<IconChevronDown size={14} />}
                        rightSectionWidth={18}
                        styles={{
                          input: {
                            width: 80,
                            fontWeight: 600,
                            textAlign: "right",
                            paddingRight: 20,
                          },
                        }}
                      />
                    }
                    value={form.values.foreignAmount || ""}
                  />
                  <Box pb={8}>
                    <IconArrowsLeftRight size={20} color="#2A81D0" />
                  </Box>
                  <TextInput
                    label={t("CONVERSION_AMOUNT")}
                    style={{ flex: 1 }}
                    radius={8}
                    readOnly
                    rightSection="VND"
                    styles={{ rightSection: { marginRight: "5px", width: 40 } }}
                    value={
                      toNum(form.values.foreignAmount) > 0 && effectiveRate
                        ? fmtVnd(calc.convertedVnd)
                        : ""
                    }
                  />
                </Flex>
                {!isDeal && (
                  <Text fz={13} c="#6B7280">
                    {t("EXCHANGE_RATE")} {currency} 1 ={" "}
                    {currencyRate ? fmtVnd(currencyRate) : "—"} {t("VND_RATE_MAY_CHANGE_AT_APPROVAL")}
                  </Text>
                )}
                {isDeal && dealChecked && (
                  <Text fz={13} c="#12B76A">
                    {t("AGREED_EXCHANGE_RATE_COLON")} {currency} 1 ={" "}
                    {fmtVnd(dealInfo?.rate || 0)} VND
                  </Text>
                )}
                {isDeal && !dealChecked && (
                  <Text fz={13} c="#6B7280">
                    {t("ENTER_QUOTE_CODE_AND_CHECK")}
                  </Text>
                )}
                {buyExceeds && (
                  <Text fz={13} c="red">
                    {t("FX_AMOUNT_MUST_NOT_EXCEED_TRANSACTION")}
                  </Text>
                )}
              </Stack>
            )}
          </Box>

          {/* Thông tin phí */}
          <Box>
            <Text fz={16} c="#212936" fw={600}>
              {t("FEE_INFORMATION") || t("FEE_INFORMATION")}
            </Text>
            <Space h={16} />
            <Radio.Group
              label={t("FEE_PAYING_PARTY")}
              value={form.values.feeType || "OUR"}
              onChange={(v) => form.setFieldValue("feeType", v)}
            >
              <Group mt="xs" spacing="lg">
                <Radio value="OUR" label={t("SENDER_PAYS_FEE")} />
                <Radio value="SHA" label={t("SHARED_FEE")} />
                <Radio value="BEN" label={t("BENEFICIARY_PAYS_FEE")} />
              </Group>
            </Radio.Group>
            <Space h={16} />
            <Text fz={14} c="#6B7280" mb={6}>
              {t("PAID_ACCOUNT")}
            </Text>
            <AccountMiniCard
              name={selectedSource?.sourceName}
              number={
                selectedSource?.sourceNumber ||
                selectedSource?.sourceObjectNumber
              }
              withChevron
            />
            <Space h={20} />

            {/* Số tiền phí ⇄ Quy đổi (giá trị tạm, chờ API tính phí) — gom trái */}
            <Flex align="center" gap={16}>
              <Stack spacing={2} w={120}>
                <Text fz={13} c="#6B7280">
                  {t("FEE_AMOUNT")}
                </Text>
                <Text fz={15} fw={600} c="#212936">
                  {fmt(calc.feeForeign, currencyScale)} {currency}
                </Text>
              </Stack>
              <IconArrowsLeftRight size={16} color="#2A81D0" />
              <Stack spacing={2}>
                <Text fz={13} c="#6B7280">
                  {t("CONVERSION")}
                </Text>
                <Text fz={15} fw={600} c="#212936">
                  {fmtVnd(calc.feeVnd)} VND
                </Text>
              </Stack>
            </Flex>
            <Space h={16} />
            {/*
              #55964 — theo yêu cầu trong comment của ticket ("Thừa thông tin Fee amount và
              Transaction amount. Bổ sung Total Amount", kèm ảnh 2066):

              · BỎ dòng "Số tiền phí" thứ hai: nó hiện `fmtVnd(calc.feeVnd)` — đúng bằng giá trị
                đã có ở ô "Quy đổi" ngay phía trên, tức cùng một số tiền phí lặp hai lần.
              · BỎ dòng "Số tiền giao dịch": người dùng đã nhập số tiền này ở ngay đầu màn.
              · THÊM dòng "Tổng số tiền" = `calc.totalForeign` — tiền chuyển cộng phần phí mà
                người chuyển chịu (OUR: đủ phí, SHA: nửa phí, BEN: không cộng), tính bằng loại
                tiền chuyển. Đây là con số duy nhất người dùng chưa tự tính được.

              Cả hai lỗi có từ commit dựng UI đầu tiên (94e59313), không phải regression.
            */}
            <Flex align="center" justify="space-between">
              <Text fz={15} fw={600} c="#2A81D0">
                {t("TOTAL_AMOUNT")}
              </Text>
              <Text fz={15} fw={700} c="#2A81D0">
                {fmt(calc.totalForeign, currencyScale)} {currency}
              </Text>
            </Flex>
          </Box>
        </Stack>
      </Box>

      {/* Footer cố định */}
      <Box
        style={{
          position: "fixed",
          bottom: 0,
          left: "calc(var(--mantine-navbar-width, 0px) + 20px)",
          right: 0,
          padding: "10px 24px",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "10px",
          boxShadow: "0px 5px 15px 0px #00000026",
          borderRadius: "12px",
          height: "76px",
          backgroundColor: "white",
          zIndex: 1,
        }}
      >
        <Flex gap={10}>
          <Button
            sx={() => ({
              width: "100px",
              backgroundColor: "#F4F4F6",
              color: "#525866",
              borderRadius: "8px",
              padding: "8px 10px",
              fontWeight: 400,
              "&:hover": { backgroundColor: "#F4F4F6" },
            })}
            onClick={openConfirm}
          >
            {t("DISMISS")}
          </Button>
          <Button
            type="submit"
            disabled={!canContinue}
            onClick={() => {
              const result = form.validateField("amount");
              if (!result.hasError && canContinue) onSubmit?.();
            }}
            sx={() => ({
              width: "160px",
              backgroundColor: "#2A81D0",
              color: "white",
              borderRadius: "8px",
              padding: "8px 10px",
              fontWeight: 400,
            })}
          >
            {t("NEXT") || t("NEXT")}
          </Button>
        </Flex>
      </Box>

      <Space h={rem(20)} />

      <Modal
        opened={confirmOpened}
        onClose={closeConfirm}
        centered
        size="sm"
        withCloseButton={false}
      >
        <Stack align="center" spacing="md" p="md">
          <Text fw={700} fz="lg" ta="center">
            {t("CONFIRM_CANCELLATION_ACTION")}
          </Text>
          <Text fz={14} ta="center" color="dimmed">
            {t("CONFIRM_CANCEL_ALL_INPUT_LOST")}
          </Text>
          <Flex gap={12} w="100%">
            <Button
              variant="outline"
              fullWidth
              color="gray"
              onClick={closeConfirm}
              style={{ borderColor: "#D1D5DB", color: "#111827" }}
            >
              {t("GO_BACK")}
            </Button>
            <Button fullWidth color="red" onClick={handleConfirmReset}>
              {t("CONFIRM")}
            </Button>
          </Flex>
        </Stack>
      </Modal>
    </>
  );
};

export default TransInterStepOne;
